/* bal-tool.js: progressive enhancement for /tools/* pages. Vanilla, no deps.
 * PRIVACY CONTRACT (do not weaken):
 *  - inputs are read from the form and used in memory only;
 *  - nothing is written to the URL, localStorage, sessionStorage, cookies or the network;
 *  - analytics events carry ONLY {tool_slug}; never an input or a result.
 * Add a new interactive tool by adding one entry to KINDS. The result renderer
 * must never name a condition as the user's outcome (no symptom-in, condition-out).
 */
(function () {
  'use strict';
  var root = document.querySelector('.bal-tool[data-tool-kind]');
  if (!root) return;
  var slug = root.getAttribute('data-tool-slug') || '';
  var kind = root.getAttribute('data-tool-kind');
  var form = root.querySelector('[data-tool-form]');
  var out = root.querySelector('[data-tool-result]');
  if (!form || !out) return;
  // controls ship disabled in the server HTML so a no-JS native submit cannot put inputs in the URL
  var ctl = form.querySelector('[data-tool-controls]');
  if (ctl) ctl.disabled = false;

  // --- consent-respecting, slug-only analytics -------------------------------
  var sent = {};
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) {}
    return false; // no consent API = no event
  }
  function track(name) {
    if (sent[name] || !analyticsAllowed()) return;
    sent[name] = true;
    var props = { tool_slug: slug }; // the ONLY property, by contract
    try { if (typeof window.gtag === 'function') window.gtag('event', name, props); } catch (e) {}
    try { if (window.clarity) window.clarity('event', name); } catch (e) {}
  }
  form.addEventListener('focusin', function () { track('tool_start'); }, { once: true });
  // testdate: the date picker cannot offer a future date (sets an attribute only; reads no input)
  var startEl = form.querySelector('input[name="start"]');
  if (startEl) { var td = new Date(); startEl.max = td.getFullYear() + '-' + ('0' + (td.getMonth() + 1)).slice(-2) + '-' + ('0' + td.getDate()).slice(-2); }

  // --- helpers ---------------------------------------------------------------
  function el(tag, text) { var n = document.createElement(tag); if (text != null) n.textContent = text; return n; }
  function fmt(mins) { mins = ((mins % 1440) + 1440) % 1440; var h = Math.floor(mins / 60), m = mins % 60; return h + ':' + (m < 10 ? '0' : '') + m; }
  function render(nodes) {
    out.textContent = ''; nodes.forEach(function (n) { out.appendChild(n); }); out.hidden = false;
    // presentation only: replay the result animation (CSS skips it under reduced motion) and bring it into view
    out.classList.remove('is-new'); void out.offsetWidth; out.classList.add('is-new');
    try { out.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
  }

  // --- tool kinds ------------------------------------------------------------
  var KINDS = {
    // probiotic-antibiotic-timing
    timing: function (fd) {
      var per = parseInt(fd.get('per_day'), 10);
      var t = String(fd.get('first_dose') || '').split(':');
      if (!(per >= 1 && per <= 4) || t.length < 2) return [el('p', 'Choose a schedule and a first dose time.')];
      var first = parseInt(t[0], 10) * 60 + parseInt(t[1], 10);
      var step = 1440 / per, doses = [];
      for (var i = 0; i < per; i++) doses.push(Math.round(first + i * step) % 1440);
      // longest gap whose midpoint is within waking hours 7:00-22:00
      var best = null;
      for (var j = 0; j < per; j++) {
        var a = first + j * step, b = first + (j + 1) * step, mid = ((a + b) / 2) % 1440;
        var awake = mid >= 420 && mid <= 1320;
        if (awake && (!best || (b - a) > best.gap)) best = { gap: b - a, mid: Math.round(mid) };
      }
      var ul = el('ul');
      doses.forEach(function (d) { ul.appendChild(el('li', 'Antibiotic: ' + fmt(d))); });
      var nodes = [el('h3', 'Your day plan'), ul];
      nodes.push(best ? el('p', 'Probiotic: around ' + fmt(best.mid) + ', midway between antibiotic doses.')
                      : el('p', 'Your doses leave no gap in waking hours; ask your pharmacist where a probiotic fits best.'));
      nodes.push(el('p', 'Take your antibiotic exactly as prescribed. Ask your pharmacist if your label gives different timing.'));
      return nodes;
    },
    // vaginal-ph-reader: explains a number; never names a condition as the outcome
    ph: function (fd) {
      var v = parseFloat(fd.get('ph'));
      if (!(v >= 3.5 && v <= 7)) return [el('p', 'Choose the number that matches your strip.')];
      var band = v <= 4.5 ? 0 : (v <= 5.0 ? 1 : 2);
      var rows = root.querySelectorAll('.bal-tool__ref tbody tr');
      var row = rows[band];
      var meaning = row ? row.children[1].textContent : '';
      var next = row ? row.children[2].textContent : '';
      return [el('h3', 'Reading ' + v.toFixed(1)), el('p', meaning), el('p', next),
              el('p', 'A pH number has several possible causes and cannot tell you why it is high or low.')];
    },
    // pregnancy-test-timing: date math only. Reads every label and rule from the server-rendered
    // reference table: row 0 expected period, 1 earliest test, 2 most reliable window (+7 to +14 days),
    // 3 retest (+7 days), 4 two negatives, 5 positive, 6 red flags. Never says whether the user is pregnant.
    testdate: function (fd) {
      var s = String(fd.get('start') || '').split('-');
      var rawLen = String(fd.get('days') || '').trim();
      var len = /^\d+$/.test(rawLen) ? parseInt(rawLen, 10) : NaN;
      if (s.length !== 3 || !(len >= 21 && len <= 45)) {
        return [el('p', 'Enter the first day of your last period and a cycle length from 21 to 45 days.')];
      }
      var y = parseInt(s[0], 10), m = parseInt(s[1], 10), d = parseInt(s[2], 10);
      var start = Date.UTC(y, m - 1, d);
      var check = new Date(start);
      if (isNaN(start) || check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) {
        return [el('p', 'Enter a valid date.')];
      }
      var now = new Date();
      if (start > Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) {
        return [el('p', 'The first day of your last period cannot be in the future.')];
      }
      function day(n) {
        return new Date(start + n * 86400000).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      }
      var rows = root.querySelectorAll('.bal-tool__ref tbody tr');
      function label(i) { return rows[i] ? rows[i].children[0].textContent : ''; }
      function rule(i) { return rows[i] ? rows[i].children[1].textContent : ''; }
      var ul = el('ul');
      ul.appendChild(el('li', label(0) + ': ' + day(len)));
      ul.appendChild(el('li', label(1) + ': ' + day(len)));
      ul.appendChild(el('li', label(2) + ': ' + day(len + 7) + ' to ' + day(len + 14)));
      ul.appendChild(el('li', label(3) + ': ' + rule(3) + ', for example ' + day(len + 7) + ' after a negative on ' + day(len) + '.'));
      ul.appendChild(el('li', label(4) + ': ' + rule(4) + '.'));
      ul.appendChild(el('li', label(5) + ': ' + rule(5) + '.'));
      var passed = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) > start + (len + 14) * 86400000;
      return [
        el('h3', 'Your estimated dates'),
        passed ? el('p', 'All of these dates have already passed. If your period still has not come, you are past the earliest suggested test day. ' + label(4) + ': ' + rule(4) + '.') : el('span'),
        ul,
        el('p', label(6) + ': ' + rule(6) + '.'),
        el('p', 'These are estimates from the dates you entered. They cannot tell whether you are pregnant; only a pregnancy test or a clinician can, and discharge cannot.')
      ];
    },
    // supplement-suitability-check: one radio pair per reference-table row (q1..qN, y/n).
    // A yes shows that row's "If yes" and "Why" cells; a no shows nothing.
    // Never says a product is safe or right for the user, never names a condition.
    yesno: function (fd) {
      var rows = root.querySelectorAll('.bal-tool__ref tbody tr');
      var flags = [], missing = [];
      for (var i = 0; i < rows.length; i++) {
        var v = fd.get('q' + (i + 1));
        if (v !== 'y' && v !== 'n') missing.push(i + 1);
        if (v === 'y') flags.push(rows[i]);
      }
      if (missing.length) {
        var first = form.querySelector('input[name="q' + missing[0] + '"]');
        if (first) { try { first.focus(); } catch (e) {} }
        return [el('p', 'Answer every question with yes or no. Still unanswered: question ' + missing.join(', ') + '.')];
      }
      if (!flags.length) {
        return [
          el('h3', 'No flags from these questions'),
          el('p', 'That does not mean a supplement is right for you. Read the full label, ask your clinician or pharmacist if you are unsure, and stop and seek care if a new symptom appears.')
        ];
      }
      var ul = el('ul');
      flags.forEach(function (r) {
        var li = el('li');
        li.appendChild(el('strong', r.children[0].textContent + ' '));
        li.appendChild(el('span', r.children[1].textContent + ' ' + r.children[2].textContent));
        ul.appendChild(li);
      });
      return [
        el('h3', flags.length === 1 ? '1 item to check with your clinician first' : flags.length + ' items to check with your clinician first'),
        ul,
        el('p', 'Bring this list and the full Supplement Facts label to your clinician or pharmacist before starting.')
      ];
    }
  };

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var fn = KINDS[kind];
    if (!fn) return;
    render(fn(new FormData(form)));
    track('tool_complete');
  });
})();
